import React, { useState, useCallback, useMemo } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import BackBtn from "../../widgets/BackBtn";
import {
  Alert,
  Container,
  Grid,
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import { getErrorResponse } from "../../utils/util";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// ─── colour tokens ────────────────────────────────────────────────────────────
const C = {
  teal: { bg: "#E1F5EE", text: "#085041", bar: "#1D9E75", mid: "#0F6E56" },
  blue: { bg: "#E6F1FB", text: "#0C447C", bar: "#378ADD", mid: "#185FA5" },
  amber: { bg: "#FAEEDA", text: "#633806", bar: "#BA7517", mid: "#854F0B" },
  gray: { bg: "#F1EFE8", text: "#444441", bar: "#888780", mid: "#5F5E5A" },
};

const DONUT_COLORS = [C.teal.bar, C.blue.bar, C.amber.bar];

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

const fmtRs = (n) =>
  n == null
    ? "—"
    : "₹" +
      Number(n).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const pct = (a, t) => (t > 0 ? ((a / t) * 100).toFixed(1) : "0.0");

// ─── sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

function KpiCard({ label, value, sub, color }) {
  return (
    <Box
      sx={{
        bgcolor: "#f9fafb",
        border: "1px solid #f3f4f6",
        borderRadius: 2,
        p: "16px",
        flex: 1,
        minWidth: 150,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 24, fontWeight: 600, color: color ?? "text.primary" }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function Badge({ label, color }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 600,
        px: "10px",
        py: "4px",
        borderRadius: "6px",
        bgcolor: C[color].bg,
        color: C[color].text,
      }}
    >
      {label}
    </Box>
  );
}

// Mini horizontal bar used inside cards
function MiniBar({ label, value, max, color, formatted }) {
  const pctWidth = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
      <Typography
        sx={{ fontSize: 11, color: "text.secondary", width: 90, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 6,
          borderRadius: "3px",
          bgcolor: "#f3f4f6",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${pctWidth}%`,
            height: "100%",
            borderRadius: "3px",
            bgcolor: color,
            transition: "width .4s ease",
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color,
          width: 56,
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {formatted}
      </Typography>
    </Box>
  );
}

function ChannelCard({
  icon,
  title,
  reachLabel,
  cost,
  rows,
  summaryBox,
  badge,
  color,
  funnelData,
}) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 3,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: C[color].bg,
            color: C[color].text,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {reachLabel}
          </Typography>
        </Box>
      </Box>

      {/* cost */}
      <Typography sx={{ fontSize: 24, fontWeight: 600, color: C[color].mid }}>
        {fmtRs(cost)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
        campaign cost
      </Typography>

      <Divider sx={{ borderColor: "#e5e7eb", mb: 1.5 }} />

      {/* rows */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          mb: 1.5,
          flex: 1,
        }}
      >
        {rows.map((r, i) => (
          <Box
            key={i}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {r.label}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              {r.value}
              {r.pct != null && (
                <Typography
                  component="span"
                  sx={{ fontSize: 11, color: "text.secondary", ml: 0.75 }}
                >
                  {r.pct}%
                </Typography>
              )}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* inline funnel mini-bars */}
      {funnelData && (
        <Box sx={{ mb: 1.5 }}>
          <Divider sx={{ borderColor: "#e5e7eb", mb: 1.25 }} />
          <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1 }}>
            Funnel depth
          </Typography>
          {funnelData.map((f, i) => (
            <MiniBar
              key={i}
              label={f.label}
              value={f.value}
              max={funnelData[0].value}
              color={f.color}
              formatted={f.formatted}
            />
          ))}
        </Box>
      )}

      {/* summary grey box for Voice / RCS */}
      {summaryBox && (
        <Box
          sx={{
            bgcolor: "#f9fafb",
            borderRadius: 2,
            p: 1.5,
            mb: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {summaryBox.map((r, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                pt: r.divider ? 1 : 0,
                borderTop: r.divider ? "1px dashed #d1d5db" : "none",
                mt: r.divider ? 0.5 : 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: r.bold ? "text.primary" : "text.secondary",
                  fontWeight: r.bold ? 500 : 400,
                }}
              >
                {r.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: r.bold ? 600 : 500,
                  color: r.bold ? "text.primary" : "text.secondary",
                }}
              >
                {r.value}
                {r.pct != null && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 11, ml: 1, fontWeight: 400 }}
                  >
                    {r.pct}%
                  </Typography>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* badge */}
      <Box sx={{ mt: "auto" }}>
        <Badge label={badge} color={color} />
      </Box>
    </Box>
  );
}

// ─── Custom donut label ───────────────────────────────────────────────────────
const DonutCenter = ({ cx, cy, totalLeads }) => (
  <>
    <text
      x={cx}
      y={cy - 8}
      textAnchor="middle"
      style={{ fontSize: 22, fontWeight: 600, fill: "#111827" }}
    >
      {fmtNum(totalLeads)}
    </text>
    <text
      x={cx}
      y={cy + 12}
      textAnchor="middle"
      style={{ fontSize: 11, fill: "#6b7280" }}
    >
      total leads
    </text>
  </>
);

// ─── Custom tooltip for stacked bar ─────────────────────────────────────────
const FunnelTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        p: 1.5,
        boxShadow: "0 4px 12px rgba(0,0,0,.08)",
        minWidth: 160,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
        {label}
      </Typography>
      {payload.map((p, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mb: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {p.name}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: p.fill }}>
            {fmtNum(p.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Spend sparkline bar ──────────────────────────────────────────────────────
function SpendBar({ label, value, total, color }) {
  const w = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,.75)" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,.9)" }}>
          {w.toFixed(0)}%
        </Typography>
      </Box>
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,.2)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${w}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 2,
          }}
        />
      </Box>
    </Box>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
const FullReportsView = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStart, setAppliedStart] = useState("");
  const [appliedEnd, setAppliedEnd] = useState("");

  const dateQuery = useMemo(() => {
    const p = [];
    if (appliedStart) p.push(`startDate=${appliedStart}`);
    if (appliedEnd) p.push(`endDate=${appliedEnd}`);
    return p.length ? `?${p.join("&")}` : "";
  }, [appliedStart, appliedEnd]);

  const applyDates = useCallback(() => {
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  }, [startDate, endDate]);

  const clearDates = useCallback(() => {
    setStartDate("");
    setEndDate("");
    setAppliedStart("");
    setAppliedEnd("");
  }, []);

  // ── data fetching ────────────────────────────────────────────────────────
  const {
    data: convData,
    isLoading: isConvLoading,
    isError: isConvError,
    error: convError,
  } = useApiEp(`/api/conversion-rates-gst`);
  const {
    data: wabaCost,
    isLoading: l1,
    isError: e1,
    error: err1,
  } = useApiEp(`/api/waba-campaign-cost${dateQuery}`);
  const {
    data: voiceCost,
    isLoading: l2,
    isError: e2,
    error: err2,
  } = useApiEp(`/api/voice-campaign-cost${dateQuery}`);
  const {
    data: rcsCost,
    isLoading: l3,
    isError: e3,
    error: err3,
  } = useApiEp(`/api/rcs-campaign-cost${dateQuery}`);
  const {
    data: wabaLeads,
    isLoading: l4,
    isError: e4,
    error: err4,
  } = useApiEp(`/api/waba-leads-count${dateQuery}`);
  const {
    data: voiceLeads,
    isLoading: l5,
    isError: e5,
    error: err5,
  } = useApiEp(`/api/voice-leads-count${dateQuery}`);
  const {
    data: rcsLeads,
    isLoading: l6,
    isError: e6,
    error: err6,
  } = useApiEp(`/api/rcs-leads-count${dateQuery}`);

  const isLoading = l1 || l2 || l3 || l4 || l5 || l6 || isConvLoading;
  const firstError = [err1, err2, err3, err4, err5, err6, convError].find(
    Boolean
  );
  const isError = e1 || e2 || e3 || e4 || e5 || e6 || isConvError;

  // ── derived metrics ──────────────────────────────────────────────────────
  const d = useMemo(() => {
    if (isLoading || isError) return null;

    const rcsConvRate = convData?.rcsConversionRate || 0.22;
    const voiceConvRate = convData?.voiceConversionRate || 0.3;
    const gstPc = convData?.gstPc || 18;
    const gstMultiplier = 1 + gstPc / 100;

    const wCost = Math.ceil(wabaCost?.totalWabaCost ?? 0);
    const vCost = Math.ceil(
      (voiceCost?.totalAmount ?? 0) * voiceConvRate * gstMultiplier
    );
    const rCost = Math.ceil(
      (rcsCost?.totalCost ?? 0) * rcsConvRate * gstMultiplier
    );
    const totalCost = wCost + vCost + rCost;

    const wMsgs = wabaLeads?.totalMessages ?? 0;
    const wSeen = wabaLeads?.totalSeen ?? 0;
    const wQual = wabaLeads?.totalQualifiedLeads ?? 0;

    const vCalls = voiceLeads?.totalCalls ?? 0;
    const vPartial = voiceLeads?.totalPartialLeads ?? 0;
    const vQual = voiceLeads?.totalFullyQualifiedLeads ?? 0;

    const rMsgs = rcsLeads?.totalMessages ?? 0;
    const rReplies = rcsLeads?.totalReplies ?? 0;
    const rSeen = rcsLeads?.totalSeen ?? 0;
    const rQual = rcsLeads?.totalLeads ?? 0;

    const totalLeads = wQual + vQual + rQual;
    const totalEngaged = wSeen + vPartial + rSeen;
    const totalReach = wMsgs + vCalls + rMsgs;
    const leadsForCpl = totalLeads + vPartial;
    const cpl = leadsForCpl > 0 ? fmtRs(totalCost / leadsForCpl) : null;

    // Stacked bar data (Recharts)
    const funnelChartData = [
      { name: "WhatsApp", Reach: wMsgs, Engaged: wSeen, Qualified: wQual },
      { name: "Voice", Reach: vCalls, Engaged: vPartial, Qualified: vQual },
      { name: "RCS", Reach: rMsgs, Engaged: rSeen, Qualified: rQual },
    ];

    // Donut data
    const donutData = [
      { name: "WhatsApp", value: wQual },
      { name: "Voice", value: vQual },
      { name: "RCS", value: rQual },
    ];

    // Per-channel funnel mini-bars
    const wabaFunnel = [
      {
        label: "Sent",
        value: wMsgs,
        color: "#9ca3af",
        formatted: fmtNum(wMsgs),
      },
      {
        label: "Seen",
        value: wSeen,
        color: C.teal.bar,
        formatted: `${fmtNum(wSeen)} (${pct(wSeen, wMsgs)}%)`,
      },
      {
        label: "Qualified",
        value: wQual,
        color: C.teal.mid,
        formatted: `${fmtNum(wQual)} (${pct(wQual, wMsgs)}%)`,
      },
    ];
    const voiceFunnel = [
      {
        label: "Calls",
        value: vCalls,
        color: "#9ca3af",
        formatted: fmtNum(vCalls),
      },
      {
        label: "Partial",
        value: vPartial,
        color: C.blue.bar,
        formatted: `${fmtNum(vPartial)} (${pct(vPartial, vCalls)}%)`,
      },
      {
        label: "DTMF",
        value: vQual,
        color: C.blue.mid,
        formatted: `${fmtNum(vQual)} (${pct(vQual, vCalls)}%)`,
      },
    ];
    const rcsFunnel = [
      {
        label: "Sent",
        value: rMsgs,
        color: "#9ca3af",
        formatted: fmtNum(rMsgs),
      },
      {
        label: "Seen",
        value: rSeen,
        color: C.amber.bar,
        formatted: `${fmtNum(rSeen)} (${pct(rSeen, rMsgs)}%)`,
      },
      {
        label: "Qualified",
        value: rQual,
        color: C.amber.mid,
        formatted: `${fmtNum(rQual)} (${pct(rQual, rMsgs)}%)`,
      },
    ];

    return {
      wCost,
      vCost,
      rCost,
      totalCost,
      wMsgs,
      wSeen,
      wQual,
      vCalls,
      vPartial,
      vQual,
      rMsgs,
      rReplies,
      rSeen,
      rQual,
      totalLeads,
      totalEngaged,
      totalReach,
      leadsForCpl,
      cpl,
      funnelChartData,
      donutData,
      wabaFunnel,
      voiceFunnel,
      rcsFunnel,
      rcsConvRate,
      voiceConvRate,
      gstPc,
    };
  }, [
    isLoading,
    isError,
    wabaCost,
    voiceCost,
    rcsCost,
    wabaLeads,
    voiceLeads,
    rcsLeads,
    convData,
  ]);

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ mt: 3, pb: 6 }}>
      <BackBtn />

      {/* ── page header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 24, fontWeight: 600, mb: 0.5, color: "#111827" }}
          >
            Campaign report
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Costs, leads, and quality across all channels
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, bgcolor: "#fff" }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, bgcolor: "#fff" }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={applyDates}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: "none",
              height: 40,
              borderColor: "#1D9E75",
              color: "#1D9E75",
            }}
          >
            Apply
          </Button>
          {(appliedStart || appliedEnd) && (
            <Button
              variant="text"
              size="small"
              onClick={clearDates}
              sx={{ color: "text.secondary" }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {isLoading && <MuPb />}
      {isError && (
        <Alert severity="error">{getErrorResponse(firstError)}</Alert>
      )}

      {d && (
        <>
          {/* ── KPI strip ── */}
          <SectionLabel>Overview</SectionLabel>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
            <KpiCard
              label="Total reach"
              value={fmtNum(d.totalReach)}
              sub="Across all channels"
            />
            <KpiCard
              label="Engaged contacts"
              value={fmtNum(d.totalEngaged)}
              sub="Read / listen ≥ 25s"
              color={C.blue.mid}
            />
            <KpiCard
              label="Qualified leads"
              value={fmtNum(d.totalLeads)}
              sub="Confirmed responses"
              color={C.teal.mid}
            />
            <KpiCard
              label="Cost per lead"
              value={d.cpl ? `${d.cpl}` : "—"}
              sub="Qual + partially qual"
            />
            <KpiCard
              label="Total spend"
              value={fmtRs(d.totalCost)}
              sub="All channels combined"
            />
          </Box>

          {/* ── Charts row: funnel bar + donut ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Grouped bar funnel */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  p: 3,
                  height: "100%",
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
                  Performance funnel by channel
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", mb: 2.5 }}
                >
                  Reach → Engaged → Qualified
                </Typography>

                {/* Custom legend */}
                <Box sx={{ display: "flex", gap: 2.5, mb: 2 }}>
                  {[
                    { label: "Reach", color: "#9ca3af" },
                    { label: "Engaged", color: "#3b82f6" },
                    { label: "Qualified", color: "#10b981" },
                  ].map((l) => (
                    <Box
                      key={l.label}
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "2px",
                          bgcolor: l.color,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        {l.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={d.funnelChartData}
                      margin={{ top: 4, right: 16, left: -8, bottom: 0 }}
                      barCategoryGap="30%"
                      barGap={3}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f3f4f6"
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 13 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) =>
                          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                        }
                      />
                      <RechartsTooltip
                        content={<FunnelTooltip />}
                        cursor={{ fill: "rgba(0,0,0,0.03)" }}
                      />
                      <Bar
                        dataKey="Reach"
                        fill="#9ca3af"
                        radius={[4, 4, 0, 0]}
                        barSize={36}
                      />
                      <Bar
                        dataKey="Engaged"
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                        barSize={36}
                      />
                      <Bar
                        dataKey="Qualified"
                        fill="#10b981"
                        radius={[4, 4, 0, 0]}
                        barSize={36}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Qualification rate mini-bars */}
                <Divider sx={{ borderColor: "#e5e7eb", my: 2 }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.secondary",
                    mb: 1.25,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Qualification rate
                </Typography>
                <MiniBar
                  label="WhatsApp"
                  value={parseFloat(pct(d.wQual, d.wMsgs))}
                  max={10}
                  color={C.teal.mid}
                  formatted={`${pct(d.wQual, d.wMsgs)}%`}
                />
                <MiniBar
                  label="Voice"
                  value={parseFloat(pct(d.vQual, d.vCalls))}
                  max={10}
                  color={C.blue.mid}
                  formatted={`${pct(d.vQual, d.vCalls)}%`}
                />
                <MiniBar
                  label="RCS"
                  value={parseFloat(pct(d.rQual, d.rMsgs))}
                  max={10}
                  color={C.amber.mid}
                  formatted={`${pct(d.rQual, d.rMsgs)}%`}
                />
              </Box>
            </Grid>

            {/* Donut: qualified lead split */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
                  Qualified lead split
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}
                >
                  Distribution by channel
                </Typography>

                <Box sx={{ flex: 1, minHeight: 200 }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={d.donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={58}
                        outerRadius={82}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {d.donutData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={DONUT_COLORS[index]}
                            stroke="none"
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => [fmtNum(value), name]}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 12px rgba(0,0,0,.08)",
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Custom legend with values */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {[
                    { label: "WhatsApp", value: d.wQual, color: C.teal.bar },
                    { label: "Voice", value: d.vQual, color: C.blue.bar },
                    { label: "RCS", value: d.rQual, color: C.amber.bar },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "2px",
                            bgcolor: item.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        {fmtNum(item.value)}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            color: "text.secondary",
                            ml: 0.75,
                          }}
                        >
                          {pct(item.value, d.totalLeads)}%
                        </Typography>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* ── channel cards ── */}
          <SectionLabel>By channel</SectionLabel>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <ChannelCard
                icon="💬"
                title="WhatsApp (WABA)"
                reachLabel={`${fmtNum(d.wMsgs)} messages sent`}
                cost={d.wCost}
                color="teal"
                badge="Button-click confirmed"
                funnelData={d.wabaFunnel}
                rows={[
                  { label: "Messages sent", value: fmtNum(d.wMsgs) },
                  {
                    label: "Read (seen)",
                    value: fmtNum(d.wSeen),
                    pct: pct(d.wSeen, d.wMsgs),
                  },
                  {
                    label: "Qualified leads",
                    value: fmtNum(d.wQual),
                    pct: pct(d.wQual, d.wMsgs),
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ChannelCard
                icon="📞"
                title="Voice"
                reachLabel={`${fmtNum(d.vCalls)} calls placed`}
                cost={d.vCost}
                color="blue"
                badge="DTMF keypress"
                funnelData={d.voiceFunnel}
                rows={[
                  { label: "Total calls", value: fmtNum(d.vCalls) },
                  {
                    label: "Partially qual (≥ 25 s)",
                    value: fmtNum(d.vPartial),
                    pct: pct(d.vPartial, d.vCalls),
                  },
                  {
                    label: "DTMF press (qualified)",
                    value: fmtNum(d.vQual),
                    pct: pct(d.vQual, d.vCalls),
                  },
                ]}
                summaryBox={[
                  {
                    label: "Rate applied",
                    value: `₹${d.voiceConvRate}/cr + ${d.gstPc}% GST`,
                  },
                  {
                    label: "Total cost",
                    value: fmtRs(d.vCost),
                    divider: true,
                    bold: true,
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ChannelCard
                icon="📩"
                title="RCS"
                reachLabel={`${fmtNum(d.rMsgs)} messages sent`}
                cost={d.rCost}
                color="amber"
                badge="Reply / action"
                funnelData={d.rcsFunnel}
                rows={[
                  {
                    label: "Read (seen)",
                    value: fmtNum(d.rSeen),
                    pct: pct(d.rSeen, d.rMsgs),
                  },
                  {
                    label: "Replies/actions (qual)",
                    value: fmtNum(d.rQual),
                    pct: pct(d.rQual, d.rMsgs),
                  },
                ]}
                summaryBox={[
                  {
                    label: "Rate applied",
                    value: `₹${d.rcsConvRate}/cr + ${d.gstPc}% GST`,
                  },
                  {
                    label: "Total cost",
                    value: fmtRs(d.rCost),
                    divider: true,
                    bold: true,
                  },
                ]}
              />
            </Grid>
          </Grid>

          {/* ── bottom layout ── */}
          <SectionLabel>Summary</SectionLabel>
          <Grid container spacing={2}>
            {/* Blended CPL with spend sparklines */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #0f766e 0%, #1e3a8a 100%)",
                  borderRadius: 3,
                  p: 3,
                  height: "100%",
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      opacity: 0.75,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      mb: 1,
                    }}
                  >
                    Blended cost per lead
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 38,
                      fontWeight: 600,
                      lineHeight: 1.1,
                      mb: 0.75,
                    }}
                  >
                    {d.cpl ? `${d.cpl}` : "—"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                    {fmtRs(d.totalCost)} across {fmtNum(d.leadsForCpl)} accepted
                    leads
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,.2)" }} />

                {/* Lead stat pills */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#4ade80",
                        }}
                      />
                      <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                        Total qualified leads
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 600, ml: 2.25 }}
                    >
                      {fmtNum(d.totalLeads)}
                    </Typography>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#60a5fa",
                        }}
                      />
                      <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                        Partially qualified leads
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 600, ml: 2.25 }}
                    >
                      {fmtNum(d.vPartial)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,.2)" }} />

                {/* Spend breakdown sparklines */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      opacity: 0.65,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      mb: 1,
                    }}
                  >
                    Spend breakdown
                  </Typography>
                  <SpendBar
                    label="WhatsApp"
                    value={d.wCost}
                    total={d.totalCost}
                    color="#4ade80"
                  />
                  <SpendBar
                    label="Voice"
                    value={d.vCost}
                    total={d.totalCost}
                    color="#60a5fa"
                  />
                  <SpendBar
                    label="RCS"
                    value={d.rCost}
                    total={d.totalCost}
                    color="#fbbf24"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Engaged contacts breakup */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  p: 3,
                  height: "100%",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
                >
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                    Engaged contacts
                  </Typography>
                  <Tooltip title="Mid-funnel intent: Seen / Listen ≥ 25s">
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: "1px solid #9ca3af",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#9ca3af",
                        fontSize: 10,
                        cursor: "help",
                      }}
                    >
                      i
                    </Box>
                  </Tooltip>
                </Box>

                {/* Three-column breakup */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    {
                      icon: "💬",
                      label: "WhatsApp",
                      sub: "Read (seen)",
                      value: d.wSeen,
                      total: d.totalEngaged,
                      color: "teal",
                    },
                    {
                      icon: "📞",
                      label: "Voice",
                      sub: "Answered (≥ 25 s)",
                      value: d.vPartial,
                      total: d.totalEngaged,
                      color: "blue",
                    },
                    {
                      icon: "📩",
                      label: "RCS",
                      sub: "Read (seen)",
                      value: d.rSeen,
                      total: d.totalEngaged,
                      color: "amber",
                    },
                  ].map((ch) => (
                    <Grid item xs={4} key={ch.label}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          mb: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: C[ch.color].bg,
                            color: C[ch.color].text,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                          }}
                        >
                          {ch.icon}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                            {ch.label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 10, color: "text.secondary" }}
                          >
                            {ch.sub}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: C[ch.color].mid,
                        }}
                      >
                        {fmtNum(ch.value)}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: "text.secondary" }}
                      >
                        {pct(ch.value, ch.total)}% of engaged
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ borderColor: "#e5e7eb", mb: 2 }} />

                {/* Engagement rate bars */}
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 1.25,
                  }}
                >
                  Engagement rate by channel
                </Typography>
                <MiniBar
                  label="WhatsApp"
                  value={parseFloat(pct(d.wSeen, d.wMsgs))}
                  max={25}
                  color={C.teal.bar}
                  formatted={`${pct(d.wSeen, d.wMsgs)}%`}
                />
                <MiniBar
                  label="Voice"
                  value={parseFloat(pct(d.vPartial, d.vCalls))}
                  max={25}
                  color={C.blue.bar}
                  formatted={`${pct(d.vPartial, d.vCalls)}%`}
                />
                <MiniBar
                  label="RCS"
                  value={parseFloat(pct(d.rSeen, d.rMsgs))}
                  max={25}
                  color={C.amber.bar}
                  formatted={`${pct(d.rSeen, d.rMsgs)}%`}
                />
              </Box>
            </Grid>

            {/* Best next action */}
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  bgcolor: "#fafafa",
                  border: "1px solid #e5e7eb",
                  borderRadius: 3,
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🎯 Best next action
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  Prioritize leads by engagement depth. Start calls with DTMF
                  keypress and RCS reply leads first — they show the highest
                  active intent.
                </Typography>

                <Divider sx={{ borderColor: "#e5e7eb", my: 2 }} />

                {/* Priority order */}
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 1.5,
                  }}
                >
                  Priority order
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {[
                    { n: 1, label: "DTMF keypresses — Voice", color: "blue" },
                    { n: 2, label: "RCS reply / action leads", color: "amber" },
                    {
                      n: 3,
                      label: "WhatsApp button-click leads",
                      color: "teal",
                    },
                  ].map((item) => (
                    <Box
                      key={item.n}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          flexShrink: 0,
                          bgcolor: C[item.color].bg,
                          color: C[item.color].text,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {item.n}
                      </Box>
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default FullReportsView;
